class FC2 {

  private events: iFC2.Events;
  private config: iFC2.Config;
  private acceptKey: string;
  private declineKey: string;
  private bannerId: string;
  private modalId: string;
  private toggleClass: string;
  private checkboxClass: string;
  private cookieName: string;
  private cookieExpiresDays: number;
  private bannerInjected: boolean;
  private modalInjected: boolean;
  private state: iFC2.State;

  /**
   * Constructor for FC2 class
   */
  constructor() {
    this.events = {};
    this.config = {
      readMoreLink: '',
      readMoreLabel: '',
      banner: {
        title: '',
        description: '',
        acceptAllLabel: '',
        declineAllLabel: '',
        customizeLabel: '',
      },
      modal: {
        title: '',
        description: '',
        acceptAllLabel: '',
        declineAllLabel: '',
        submitLabel: '',
      },
      sections: [],
    };
    this.acceptKey = 'accept';
    this.declineKey = 'decline';
    this.bannerId = 'fc2-banner';
    this.modalId = 'fc2-modal';
    this.toggleClass = 'fc2-js-toggle';
    this.checkboxClass = 'fc2-js-checkbox';
    this.cookieName = 'fc2';
    this.cookieExpiresDays = 30;
    this.bannerInjected = false;
    this.modalInjected = false;
    this.state = {};
  }

  /**
   * Listen for a event called `eventName` and execute the `fn` callback function
   */
  private on(eventName: string, fn:iFC2.EventCallback): void {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    this.events[eventName].push(fn);
  }

  /**
   * Emit the event called `eventName` with optional `data`.
   * This calls every `fn` callback function registered
   * for event `eventName`
   */
  private emit(eventName: string, data: object = {}) {
    const event: Array<iFC2.EventCallback> = this.events[eventName];
    if (event) {
      event.forEach((fn) => {
        fn.call(null, data);
      });
    }
  }

  /**
   * Execute the `fn` callback function when the service matching `serviceKey` is Accepted
   */
  public onAccept(serviceKey: string, fn:iFC2.EventCallback): void {
    this.on(`${serviceKey}:${this.acceptKey}`, fn);
  }

  /**
   * Execute the `fn` callback function when the service matching `serviceKey` is Declined
   */
  public onDecline(serviceKey: string, fn: iFC2.EventCallback): void {
    this.on(`${serviceKey}:${this.declineKey}`, fn);
  }

  /**
   * Emit the Accept event for the service matching `serviceKey`
   */
  private emitAccept(serviceKey: string): void {
    const data: iFC2.Service|{} = this.getService(serviceKey);
    this.emit(`${serviceKey}:${this.acceptKey}`, data);
  }

  /**
   * Emit the Decline event for the service matching `serviceKey`
   */
  private emitDecline(serviceKey: string): void {
    const data: iFC2.Service|{} = this.getService(serviceKey);
    this.emit(`${serviceKey}:${this.declineKey}`, data);
  }

  /**
   * Emit all Decline and Accept events from the state's values
   */
  private emitEventsFromState() {
    Object.keys(this.state).forEach((key: string) => {
      if (this.state[key]) {
        this.emitAccept(key);
      } else {
        this.emitDecline(key);
      }
    });
  }

  /**
   * Retrieve info for service matching serviceKey
   */
  public getService(serviceKey: string): iFC2.Service|{} {
    let result = {};
    this.config.sections.forEach((section) => {
      section.services.forEach((service) => {
        if (service.key === serviceKey) {
          result = service;
        }
      });
    });
    return result;
  }

  /**
   * load script and stylesheet utils
   */
  static loadScript(url: string, async: boolean = true): void {
    const { head } = document;
    const script = document.createElement('script');
    script.async = async;
    script.src = url;

    head.appendChild(script);
  }

  /**
   * Init FC2 with config
   */
  public init(config: iFC2.Config) {
    this.config = Object.assign(this.config, config);
    if (document.readyState === "complete") {
      this.setStateFromCookie();
      if (!this.wasSubmited()) {
        this.showBanner();
      }
    } else {
      window.addEventListener('load', () => {
        this.setStateFromCookie();
        if (!this.wasSubmited()) {
          this.showBanner();
        }
      });
    }
  }

  /**
   * Convert an HTML string into a ChildNode
   */
  static htmlToChildNode(html: string): ChildNode|null {
    const template = document.createElement('template');
    const cleanedHtml = html.trim();
    template.innerHTML = cleanedHtml;
    if (template.content) {
      return template.content.firstChild;
    } else {
      return template.firstChild;
    }
  }

  /**
   * Inject Banner into DOM
   */
  private injectBanner() {
    const bannerElement: ChildNode|null = FC2.htmlToChildNode(`
      <div class="fc2-banner fc2-wrapper" id="${this.bannerId}">
      <div class="fc2-header">
        <p class="fc2-title">${this.config.banner.title}</p>
      </div>
      <div class="fc2-body">
        <p>${this.config.banner.description}</p>
        <a class="fc2-link" href="${this.config.readMoreLink}">${this.config.readMoreLabel}</a>
        <hr>
      </div>
      <div class="fc2-footer">
        <button class="fc2-button" onclick="fc2.showModal(); fc2.hideBanner();">${this.config.banner.customizeLabel}</button>
        <button class="fc2-button" onclick="fc2.declineAll(); fc2.hideBanner();">${this.config.banner.declineAllLabel}</button>
        <button class="fc2-button" onclick="fc2.acceptAll(); fc2.hideBanner();">${this.config.banner.acceptAllLabel}</button>
      </div>
      </div>
    `);
    if (bannerElement) {
      document.body.appendChild(bannerElement);
    }
  }

  /**
   * Get HTML string for services
   */
  private getServicesHTML(services: Array<iFC2.Service>) {
    const servicesHTML = services.map((service) => {
      const checked: string = this.state[service.key] ? ' checked' : '';
      return `
        <li class="fc2-collapse-item">
          <div class="fc2-collapse-header">
            <p class="fc2-title ${this.toggleClass}"><span class="fc2-collapse-icon"></span>${service.title}</p>
            <div class="fc2-switch">
              <input type="checkbox" name="${service.key}"${checked} class="${this.checkboxClass}">
              <div class="fc2-switch-label"></div>
            </div>
          </div>
          <div class="fc2-collapse-body">
            <p>${service.description}</p>
          </div>
        </li>
      `;
    });
    return servicesHTML.join('\n');
  }

  /**
   * Get HTML string for sections
   */
  private getSectionsHTML(): string {
    const sectionsHTML: Array<string> = this.config.sections.map((section: iFC2.Section): string => {
      const services: string = this.getServicesHTML(section.services);
      return `
        <li class="fc2-list-item">
          <div class="fc2-list-header">
            <p class="fc2-title">${section.title}</p>
          </div>
          <div class="fc2-list-body">
            <ul class="fc2-collapse">
              ${services}
            </ul>
          </div>
        </li>
      `;
    });
    return sectionsHTML.join('\n');
  }

  /**
   * Inject Modal into DOM
   */
  private injectModal(): void {
    const modalElement: ChildNode|null = FC2.htmlToChildNode(`
      <div class="fc2-modal fc2-wrapper" id="${this.modalId}">
        <div class="fc2-header">
          <p class="fc2-title">${this.config.modal.title}</p>
        </div>
        <div class="fc2-header">
          <p>${this.config.modal.description}</p>
        </div>
        <div class="fc2-body">
          <ul class="fc2-list">
            ${this.getSectionsHTML()}
          </ul>
          <a class="fc2-link" href="${this.config.readMoreLink}">${this.config.readMoreLabel}</a>
          <hr>
        </div>
        <div class="fc2-footer">
          <button class="fc2-button" onclick="fc2.acceptAll(); fc2.hideModal(500);">${this.config.modal.acceptAllLabel}</button>
          <button class="fc2-button" onclick="fc2.declineAll(); fc2.hideModal(500);">${this.config.modal.declineAllLabel}</button>
          <button class="fc2-button" onclick="fc2.submit(); fc2.hideModal(500);">${this.config.modal.submitLabel}</button>
        </div>
      </div>
    `);
    if (modalElement) {
      document.body.appendChild(modalElement);
    }
    this.bindItemToggle();
  }

  /**
   * Bind click on services to display/hide description
   */
  private bindItemToggle(): void {
    const itemElements: HTMLCollectionOf<Element> = document.getElementsByClassName(this.toggleClass);
    // we use a basic for loop insteand of Array.from to keep compatibility with old browsers
    for (let i:number = 0; i < itemElements.length; i++) {
      itemElements[i].addEventListener('click', function (this: Element) {
        if (this.parentNode && this.parentNode.parentNode) {
          (this.parentNode.parentNode as Element).classList.toggle('active');
        }
      });
    }
  }

  /**
   * Return banner HTMLElement
   */
  public getBannerElement(): HTMLElement|null {
    return document.getElementById(this.bannerId);
  }

  /**
   * Return modal HTMLElement
   */
  public getModalElement(): HTMLElement|null {
    return document.getElementById(this.modalId);
  }

  /**
   * Show banner
   */
  public showBanner(): void {
    if (!this.bannerInjected) {
      this.injectBanner();
      this.bannerInjected = true;
    }
    let bannerElement: HTMLElement|null = this.getBannerElement();
    if (bannerElement) {
      bannerElement.classList.add('active');
    }
  }

  /**
   * Hide banner
   */
  public hideBanner(): void {
    let bannerElement: HTMLElement|null = this.getBannerElement();
    if (bannerElement) {
      bannerElement.classList.remove('active');
    }
  }

  /**
   * Show modal
   */
  public showModal(): void {
    if (!this.modalInjected) {
      this.injectModal();
      this.modalInjected = true;
    }
    let modalElement: HTMLElement|null = this.getModalElement();
    if (modalElement) {
      modalElement.classList.add('active');
    }
  }

  /**
   * Hide modal
   */
  public hideModal(timeout: number = 0): void {
    setTimeout(() => {
      let modalElement: HTMLElement|null = this.getModalElement();
      if (modalElement) {
        modalElement.classList.remove('active');
      }
    }, timeout);
  }

  /**
   * Set FC2 cookie with given value
   */
  private setCookie(value = ''): void {
    const date: Date = new Date();
    date.setTime(date.getTime() + (this.cookieExpiresDays * 24 * 60 * 60 * 1000));
    const expires: string = date.toUTCString();
    document.cookie = `${this.cookieName}=${value}; expires=${expires}; path=/`;
  }

  /**
   * Delete FC2 cookie
   */
  public deleteCookie(): void {
    const date: Date = new Date();
    date.setTime(date.getTime() - (1 * 24 * 60 * 60 * 1000));
    const expires: string = date.toUTCString();
    document.cookie = `${this.cookieName}=; expires=${expires}; path=/`;
  }

  /**
   * Get FC2 cookie value
   */
  private getCookie(): string {
    const name: string = `${this.cookieName}=`;
    const cookiesParts: Array<string> = document.cookie.split(';');
    let value: string = '';
    cookiesParts.forEach((cookiePart: string): void => {
      let cleanedCookiePart: string = cookiePart;
      while (cleanedCookiePart.charAt(0) === ' ') {
        cleanedCookiePart = cleanedCookiePart.substring(1, cookiePart.length);
      }
      if (cleanedCookiePart.indexOf(name) === 0) {
        value = cleanedCookiePart.substring(name.length, cleanedCookiePart.length);
      }
    });
    return value;
  }

  /**
   * Retrieve value from Cookie
   * and set it on the `state` property
   */
  private setStateFromCookie(): void {
    const cookieValue: string = this.getCookie();
    if (cookieValue) {
      this.state = cookieValue.split('|')
        .reduce((prev: iFC2.State, current: string) => {
          const arr: Array<string> = current.split('=');
          prev[arr[0]] = !!+arr[1];
          return prev;
        }, {});
      this.emitEventsFromState();
    }
  }

  /**
   * Set value from the `state` property
   * to the cookie
   * Value is stored with the following format
   * `FooServiceKey=1|BarServiceKey=0|BazServiceKey=1`
   */
  private setCookieFromState(): void {
    const cookieValue: string = Object.keys(this.state)
      .reduce((prev: Array<string>, key: string): Array<string> => {
        const val: number = this.state[key] ? 1 : 0;
        prev.push(`${key}=${val}`);
        return prev;
      }, []).join('|');
    this.setCookie(cookieValue);
    this.emitEventsFromState();
  }

  /**
   * Return true is the modal was submitted by the user
   */
  public wasSubmited(): boolean {
    return Object.keys(this.state).length > 0;
  }

  /**
   * Return true is the service matching serviceKey was accepted
   */
  public isAccepted(serviceKey: string): boolean {
    return this.state[serviceKey] || false;
  }

  /**
   * Return true is the service matching serviceKey was declined
   */
  public isDeclined(serviceKey: string): boolean {
    return !this.isAccepted(serviceKey);
  }

  /**
   * Accept all services
   */
  public acceptAll(): void {
    const checkboxesElements: HTMLCollectionOf<HTMLInputElement> = (document.getElementsByClassName(this.checkboxClass) as HTMLCollectionOf<HTMLInputElement>);
    if (checkboxesElements.length > 0) {
      // we use a basic for loop insteand of Array.from to keep compatibility with old browsers
      for (let i:number = 0; i < checkboxesElements.length; i++) {
        const key: string|null = checkboxesElements[i].getAttribute('name');
        checkboxesElements[i].checked = true;
        if (key) {
          this.state[key] = true;
        }
      }
    } else {
      this.config.sections.forEach((section) => {
        section.services.forEach((service) => {
          this.state[service.key] = true;
        });
      });
    }
    this.setCookieFromState();
  }

  /**
   * Decline all services
   */
  public declineAll(): void {
    const checkboxesElements: HTMLCollectionOf<HTMLInputElement> = (document.getElementsByClassName(this.checkboxClass) as HTMLCollectionOf<HTMLInputElement>);
    if (checkboxesElements.length > 0) {
      // we use a basic for loop insteand of Array.from to keep compatibility with old browsers
      for (let i:number = 0; i < checkboxesElements.length; i++) {
        const key: string|null = checkboxesElements[i].getAttribute('name');
        checkboxesElements[i].checked = false;
        if (key) {
          this.state[key] = false;
        }
      }
    } else {
      this.config.sections.forEach((section) => {
        section.services.forEach((service) => {
          this.state[service.key] = false;
        });
      });
    }
    this.setCookieFromState();
  }

  /**
   * Submit choices in modal
   */
  public submit(allTrue = false): void {
    const checkboxesElements: HTMLCollectionOf<HTMLInputElement> = (document.getElementsByClassName(this.checkboxClass) as HTMLCollectionOf<HTMLInputElement>);
    // we use a basic for loop insteand of Array.from to keep compatibility with old browsers
    for (let i:number = 0; i < checkboxesElements.length; i++) {
      const key: string|null = checkboxesElements[i].getAttribute('name');
      const value: boolean = checkboxesElements[i].checked;
      if (key) {
        this.state[key] = value || allTrue;
      }
    }
    this.setCookieFromState();
  }
}

const fc2 = new FC2();
export default fc2;